import { PollQuestion } from '../../shared/dto/EntityRepository';
import { PollQuestionType } from '../../shared/enum/EnumRepository';
import { POLL_OTHER_MAX_LENGTH, POLL_OTHER_MAX_LENGTH_LONG } from '../../shared/constants/limits';

export interface PollQuestionsData {
  version: string;
  questions: PollQuestion[];
}

export interface PollCommonData {
  title: string;
  subTitle: string;
  submitButton: string;
  submitMessage: string;
}

export interface PollData extends PollCommonData, PollQuestionsData {}

// 공통 데이터
const commonData_ko: PollCommonData = {
  title: "Feedback",
  subTitle: "더 나은 서비스를 위해 의견을 들려주세요.",
  submitButton: "제출하기",
  submitMessage: "설문이 완료되었습니다. 참여해 주셔서 감사합니다."
};

const commonData_en: PollCommonData = {
  title: "Feedback",
  subTitle: "Your voice helps us shape a better service.",
  submitButton: "Submit",
  submitMessage: "Thank you for sharing your thoughts."
};

// 질문 데이터만
const questionsData_1_ko: PollQuestionsData = {
  "version": "0.2.0",
  "questions": [
    {
      "id": "v1_q1",
      "title": "1. Architrave를 어디서 처음 알게 되었나요?",
      "type": PollQuestionType.SINGLE,
      "options": [
        { "value": "instagram", "label": "Instagram" },
        { "value": "friend", "label": "지인 소개" },
        { "value": "other", "label": "기타", "hasOther": true, "otherMaxLength": POLL_OTHER_MAX_LENGTH }
      ]
    },
    {
      "id": "v1_q2",
      "title": "2. Instagram @architrave.work에서 소식을 확인할 수 있다는 걸 알고 있나요?",
      "type": PollQuestionType.SINGLE,
      "options": [
        { "value": "yes", "label": "네, 알고 있습니다." }
      ]
    },
    {
      "id": "v1_q3",
      "title": "3. 주로 어떤 목적으로 Architrave를 사용하려 하나요?",
      "type": PollQuestionType.SINGLE,
      "options": [
        { "value": "archive", "label": "작품 아카이빙" },
        { "value": "portfolio", "label": "포트폴리오" },
        { "value": "archive-portfolio", "label": "작품 아카이빙 + 포트폴리오" },
        { "value": "other", "label": "기타", "hasOther": true, "otherMaxLength": POLL_OTHER_MAX_LENGTH }
      ]
    },
    {
      "id": "v1_q4",
      "title": "4. 어떤 방식의 구독이 좋은가요?",
      "type": PollQuestionType.SINGLE,
      "options": [
        { "value": "fixed", "label": "정액 구독제 (예: 월 $10)" },
        { "value": "tiered", "label": "사용량에 따라 달라지는 구독제 (예: $5, $10, $20, $100/월)" }
      ]
    },
    {
      "id": "v1_q5",
      "title": "5. 앞으로 어떤 기능이 먼저 도입되면 좋을까요?",
      "type": PollQuestionType.SINGLE,
      "options": [
        { "value": "multi-lang", "label": "다국어 지원" },
        { "value": "mobile-optimize", "label": "모바일 최적화" },
        { "value": "service-stability", "label": "안정적인 서비스 운영" },
        { "value": "other", "label": "기타", "hasOther": true, "otherMaxLength": POLL_OTHER_MAX_LENGTH_LONG }
      ]
    },
    {
      "id": "v1_q6",
      "title": "6. Architrave가 어떤 방향으로 확장되면 좋을까요?",
      "type": PollQuestionType.SINGLE,
      "options": [
        { "value": "expand-service-coverage", "label": "큐레이터·비평가·전시공간 등 미술계 전반 지원" },
        { "value": "expand-institution", "label": "갤러리·미술관 등 기관 활용 확대" },
        { "value": "develop-portfolio", "label": "포트폴리오 기능 다양화" }
      ]
    }
  ]
};

const questionsData_1_en: PollQuestionsData = {
  "version": "0.2.0",
  "questions": [
    {
      "id": "v1_q1",
      "title": "1. How did you first hear about Architrave?",
      "type": PollQuestionType.SINGLE,
      "options": [
        { "value": "instagram", "label": "Instagram" },
        { "value": "friend", "label": "Friend's recommendation" },
        { "value": "other", "label": "Other", "hasOther": true, "otherMaxLength": POLL_OTHER_MAX_LENGTH }
      ]
    },
    {
      "id": "v1_q2",
      "title": "2. Did you know updates are shared on Instagram @architrave.work?",
      "type": PollQuestionType.SINGLE,
      "options": [
        { "value": "yes", "label": "Yes" }
      ]
    },
    {
      "id": "v1_q3",
      "title": "3. What is your main purpose for using Architrave?",
      "type": PollQuestionType.SINGLE,
      "options": [
        { "value": "archive", "label": "Archive artwork" },
        { "value": "portfolio", "label": "Portfolio" },
        { "value": "archive-portfolio", "label": "Archive + Portfolio" },
        { "value": "other", "label": "Other", "hasOther": true, "otherMaxLength": POLL_OTHER_MAX_LENGTH }
      ]
    },
    {
      "id": "v1_q4",
      "title": "4. Which subscription feels right for you?",
      "type": PollQuestionType.SINGLE,
      "options": [
        { "value": "fixed", "label": "Fixed (e.g., $10/month)" },
        { "value": "tiered", "label": "Usage-based (e.g., $5–$100/month)" }
      ]
    },
    {
      "id": "v1_q5",
      "title": "5. Which feature would be most helpful?",
      "type": PollQuestionType.SINGLE,
      "options": [
        { "value": "multi-lang", "label": "Multi-language" },
        { "value": "mobile-optimize", "label": "Mobile support" },
        { "value": "service-stability", "label": "Service stability" },
        { "value": "other", "label": "Other", "hasOther": true, "otherMaxLength": POLL_OTHER_MAX_LENGTH_LONG }
      ]
    },
    {
      "id": "v1_q6",
      "title": "6. In which direction should Architrave grow?",
      "type": PollQuestionType.SINGLE,
      "options": [
        { "value": "expand-service-coverage", "label": "Support for curators, critics, and exhibition spaces" },
        { "value": "expand-institution", "label": "Enable use by galleries and museums" },
        { "value": "develop-portfolio", "label": "Diversify portfolio features for artists" }
      ]
    }
  ]
};

// 최종 데이터 (공통 데이터 + 질문 데이터)
export const pollData_1_ko: PollData = {
  ...commonData_ko,
  ...questionsData_1_ko
};

export const pollData_1_en: PollData = {
  ...commonData_en,
  ...questionsData_1_en
};
